import { Box, PopoverProps, Tooltip } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import React, { forwardRef, useCallback } from 'react';

import { biTooltipClose, biTooltipLoad } from '../../bi';
import { WIDGET_DRAG_ICON, WIDGET_DRAG_ICON_TOOLTIP } from '../../constants';
import { getState } from '../../store';
import { DragIconSvg } from './DragIconSvg';

type DragIconProps = Readonly<{
  isDetached: boolean | undefined;
  isEditor3?: boolean;
  isStudio?: boolean;
}>;

const dragIconTooltipMoveBy: PopoverProps['moveBy'] = { y: 6 };
const biTooltipName = 'widget-drag-icon-tooltip';

export const DragIcon = forwardRef<HTMLDivElement, DragIconProps>(
  ({ isDetached, isEditor3, isStudio }, ref) => {
    const { t } = useTranslation();
    const { initParams } = getState();
    // tooltip only appears before the user drags the widget;
    const tooltipDisabled = !initParams.isInvokedFromHeader || isDetached;
    const dragIconColor = isEditor3 ? '#F4F4F4' : isStudio ? 'black' : 'white';
    const onShown = useCallback(() => {
      biTooltipLoad({
        title: biTooltipName,
        content: t('drag_icon_tooltip_content'),
      });
    }, [t]);

    const onHide = useCallback(() => {
      if (tooltipDisabled) {
        // onHide is triggered on mouseleave event, even when tooltip was not shown because of disabled state
        return;
      }
      biTooltipClose({
        title: biTooltipName,
        content: t('drag_icon_tooltip_content'),
      });
    }, [t, tooltipDisabled]);

    return (
      <Box position="absolute" width="100%" align="center">
        <Tooltip
          content={t('drag_icon_tooltip_content')}
          size="small"
          maxWidth="100%"
          moveBy={dragIconTooltipMoveBy}
          disabled={tooltipDisabled}
          onShow={onShown}
          onHide={onHide}
          dataHook={WIDGET_DRAG_ICON_TOOLTIP}
          fluid
        >
          <Box
            ref={ref}
            cursor="move"
            color={dragIconColor}
            dataHook={WIDGET_DRAG_ICON}
            width="100%"
            align="center"
          >
            <DragIconSvg />
          </Box>
        </Tooltip>
      </Box>
    );
  },
);
